import { TextField } from "@mui/material";
import { Controller, Control, FieldValues } from "react-hook-form";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import BoxKit from "@/shared/components/kit/box";

type ProfileFormSectionProps<T extends FieldValues> = {
  fields: { label: string; name: keyof T }[];
  control: Control<T>;
  isEditing: boolean;
  faceData: T; // we assume single object per call here
};
/**
 * ProfileFormSection
 *
 * این کامپوننت یک بخش مستقل از فرم پروفایل را بر اساس داده‌های ورودی و وضعیت ویرایش نمایش می‌دهد.
 * ورودی‌ها شامل لیستی از فیلدها، کنترل‌کننده React Hook Form، وضعیت ویرایش، و داده‌های اصلی فرم هستند.
 *
 * این بخش:
 * - در صورت وجود فیلدی با نام "title"، آن را به عنوان عنوان بخش با استایل هدر نمایش می‌دهد.
 * - سایر فیلدها را به صورت پویا رندر می‌کند.
 * - در حالت ویرایش (`isEditing === true`) فیلدها را به صورت TextField قابل تغییر نمایش می‌دهد.
 * - در حالت غیر ویرایش مقدار نهایی فیلدها را به صورت متن نمایش می‌دهد.
 *
 * @template T نوع جنریک داده‌ها که باید از FieldValues مشتق شده باشد.
 *
 * @param fields آرایه‌ای از فیلدها شامل label و name
 * @param control کنترل React Hook Form برای مدیریت داده‌ها و اعتبارسنجی
 * @param isEditing تعیین‌کننده حالت نمایش (ویرایش / فقط نمایش)
 * @param faceData داده‌های اصلی هر بخش برای نمایش مقدار اولیه
 *
 * @returns JSX.Element
 */

export default function ProfileFormSection<T extends FieldValues>({ fields, control, isEditing, faceData,}: ProfileFormSectionProps<T>) {
  const titleField = fields.find((f) => f.name === "title");
  const otherFields = fields.filter((f) => f.name !== "title");

  return (
    <StackKit direction="column" sx={{ width: "100%", gap: 1, mb: 2,padding:2,justifyContent:'space-between' }}>
      {/* Render title as H5 header */}
      {titleField && (
        <TypoGraphyKit textAlign={'right'} variant="h6" fontWeight={'bold'} sx={{ mb: 1, color: "#ccc" }}>
          {String((faceData as any)[titleField.name])}
        </TypoGraphyKit>
      )}

      {/* Render the other fields */}
      {otherFields.map((field) => (
        <BoxKit key={String(field.name)} sx={{width:"100%",display:'flex',justifyContent:'space-between',flexDirection:isEditing ? 'column' : {sm:'row',xs:'column'}}} gap={2}>
          <TypoGraphyKit variant="body1" sx={{ color: "#ccc", mb: { xs: 0.5, md: 0 } }}>
            {field.label} :
          </TypoGraphyKit>

          {isEditing ? (
            <Controller name={field.name as any} control={control} render={({ field: inputField }) => (<TextField {...inputField} size="small" variant="outlined"/>)}/>
          ) : (
            <TypoGraphyKit variant="body1" sx={{ color: "#eee"}}>
              {String((faceData as any)[field.name] ?? "")}
            </TypoGraphyKit>
          )}
        </BoxKit>
      ))}
    </StackKit>
  );
}
